import { roundToStrike } from "@/lib/options/structure";
import type { Mode, Regime } from "@/types/options";
import { defineStrategy } from "./define";
import { leg } from "./leg";
import { offsetParam, toggleParam } from "./params";
import { scale } from "./scale";

// ── Synthetic Future ───────────────────────────────────────────────────────
// side:  1 = long (long call + short put)
// side: -1 = short (short call + long put)

export const syntheticFuture = defineStrategy(
  "synthetic_future",
  "Synthetic Future",
  "synthetic",
  {
    defaultParams: () => ({ side: 1, strikeOffset: 0 }),
    paramSchema: [
      toggleParam("side", "Side", [
        [-1, "Short"],
        [1, "Long"],
      ]),
      offsetParam("strikeOffset", "Strike Offset", -50, 50),
    ],
    scaleParams: (r: Regime, m: Mode, cur: Record<string, number>) => ({
      side: cur.side ?? 1,
      strikeOffset: scale(0, r, m, true, 50),
    }),
    toLegs: (spot, params, strikeStep) => {
      const strike = roundToStrike(spot, strikeStep) + params.strikeOffset;
      const callSide: "long" | "short" = params.side >= 0 ? "long" : "short";
      const putSide: "long" | "short" = params.side >= 0 ? "short" : "long";
      return [leg(strike, "call", callSide), leg(strike, "put", putSide)];
    },
  },
);

// ── Combo ──────────────────────────────────────────────────────────────────
// side:  1 = long (long OTM call + short OTM put)
// side: -1 = short (short OTM call + long OTM put)

export const combo = defineStrategy("combo", "Combo", "synthetic", {
  defaultParams: () => ({
    side: 1,
    callOffset: 20,
    putOffset: 20,
  }),
  paramSchema: [
    toggleParam("side", "Side", [
      [-1, "Short"],
      [1, "Long"],
    ]),
    offsetParam("callOffset", "Call Offset", 5, 80, 0),
    offsetParam("putOffset", "Put Offset", 5, 80, 1),
  ],
  scaleParams: (r: Regime, m: Mode, cur: Record<string, number>) => ({
    side: cur.side ?? 1,
    callOffset: scale(20, r, m, true),
    putOffset: scale(20, r, m, true),
  }),
  toLegs: (spot, params, strikeStep) => {
    const center = roundToStrike(spot, strikeStep);
    const callSide: "long" | "short" = params.side >= 0 ? "long" : "short";
    const putSide: "long" | "short" = params.side >= 0 ? "short" : "long";
    return [
      leg(center + params.callOffset, "call", callSide),
      leg(center - params.putOffset, "put", putSide),
    ];
  },
});

// ── Strip ──────────────────────────────────────────────────────────────────
// Bearish weighted straddle: 1 call + 2 puts

export const strip = defineStrategy("strip", "Strip", "volatility", {
  defaultParams: () => ({ strikeOffset: 0 }),
  paramSchema: [offsetParam("strikeOffset", "Strike Offset", -50, 50)],
  scaleParams: (r: Regime, m: Mode) => ({
    strikeOffset: scale(0, r, m, true, 50),
  }),
  toLegs: (spot, params, strikeStep) => {
    const strike = roundToStrike(spot, strikeStep) + params.strikeOffset;
    return [leg(strike, "call", "long"), leg(strike, "put", "long", 2)];
  },
});

// ── Strap ──────────────────────────────────────────────────────────────────
// Bullish weighted straddle: 2 calls + 1 put

export const strap = defineStrategy("strap", "Strap", "volatility", {
  defaultParams: () => ({ strikeOffset: 0 }),
  paramSchema: [offsetParam("strikeOffset", "Strike Offset", -50, 50)],
  scaleParams: (r: Regime, m: Mode) => ({
    strikeOffset: scale(0, r, m, true, 50),
  }),
  toLegs: (spot, params, strikeStep) => {
    const strike = roundToStrike(spot, strikeStep) + params.strikeOffset;
    return [leg(strike, "call", "long", 2), leg(strike, "put", "long")];
  },
});

// ── Guts ───────────────────────────────────────────────────────────────────
// side:  1 = long (buy ITM call + buy ITM put)
// side: -1 = short (sell ITM call + sell ITM put)

export const guts = defineStrategy("guts", "Guts", "volatility", {
  defaultParams: () => ({
    side: 1,
    callOffset: 20,
    putOffset: 20,
  }),
  paramSchema: [
    toggleParam("side", "Side", [
      [-1, "Short"],
      [1, "Long"],
    ]),
    offsetParam("callOffset", "Call ITM Offset", 5, 80, 0),
    offsetParam("putOffset", "Put ITM Offset", 5, 80, 1),
  ],
  scaleParams: (r: Regime, m: Mode, cur: Record<string, number>) => ({
    side: cur.side ?? 1,
    callOffset: scale(20, r, m, true),
    putOffset: scale(20, r, m, true),
  }),
  toLegs: (spot, params, strikeStep) => {
    const center = roundToStrike(spot, strikeStep);
    const s: "long" | "short" = params.side >= 0 ? "long" : "short";
    return [
      leg(center - params.callOffset, "call", s),
      leg(center + params.putOffset, "put", s),
    ];
  },
});
